import type { JSX } from "react";
import { isValidElement } from "react";

import { SafetyPlanTestID } from "@/constants/TestID";
import SafetyPlanSection, {
  BulletPointText,
  LinkText,
} from "@/ui/components/profile/safetyPlan/SafetyPlanSection";
import type { LinkPressProps } from "@/ui/components/profile/safetyPlan/SafetyPlanSection";
import { Box, HStack } from "@/ui/layout";
import { joinTestIDs } from "@/utils/string.util";

interface HotlinesSectionProps {
  crisisNumber?: string;
  domesticViolenceNumber?: string;
  onLinkPress: (props: LinkPressProps) => void;
  trevorProjectNumber?: string;
}

const HotlinesSection = ({
  crisisNumber,
  domesticViolenceNumber,
  onLinkPress,
  trevorProjectNumber,
}: HotlinesSectionProps) => {
  const hotlines = [
    crisisNumber && (
      <HStack key={joinTestIDs(SafetyPlanTestID.Hotlines, "suicide")}>
        <BulletPointText
          testID={joinTestIDs(SafetyPlanTestID.Hotlines, "dial-national")}
          text="National Suicide & Crisis Lifeline: Dial "
        />
        <LinkText
          linkType="tel"
          onPress={() => {
            onLinkPress({
              field: joinTestIDs(HotlinesSection.name, "suicide"),
              linkType: "tel",
              text: crisisNumber,
            });
          }}
          testID={joinTestIDs(SafetyPlanTestID.Hotlines, "suicide")}
          text={crisisNumber}
        />
      </HStack>
    ),
    domesticViolenceNumber && (
      <HStack key={joinTestIDs(SafetyPlanTestID.Hotlines, "domestic-violence")}>
        <Box flexShrink={1}>
          <BulletPointText
            testID={joinTestIDs(SafetyPlanTestID.Hotlines, "domestic-violence")}
            text="National Domestic Violence Hotline: "
          />
          <LinkText
            linkType="tel"
            onPress={() => {
              onLinkPress({
                field: joinTestIDs(HotlinesSection.name, "domestic-violence"),
                linkType: "tel",
                text: domesticViolenceNumber,
              });
            }}
            testID={joinTestIDs(
              SafetyPlanTestID.Hotlines,
              "domestic-violence-url",
            )}
            text={domesticViolenceNumber}
          />
        </Box>
      </HStack>
    ),
    trevorProjectNumber && (
      <HStack key={joinTestIDs(SafetyPlanTestID.Hotlines, "trevor-project")}>
        <Box flexShrink={1}>
          <BulletPointText
            testID={joinTestIDs(SafetyPlanTestID.Hotlines, "trevor-project")}
            text="The Trevor Project Hotline: "
          />
          <LinkText
            linkType="tel"
            onPress={() => {
              onLinkPress({
                field: joinTestIDs(HotlinesSection.name, "trevor-project"),
                linkType: "tel",
                text: trevorProjectNumber,
              });
            }}
            testID={joinTestIDs(
              SafetyPlanTestID.Hotlines,
              "trevor-project-url",
            )}
            text={trevorProjectNumber}
          />
        </Box>
      </HStack>
    ),
  ].filter(
    (element): element is JSX.Element =>
      Boolean(element) && isValidElement(element),
  );

  return (
    <SafetyPlanSection
      shouldPrefixWithBullet
      statements={hotlines}
      testID={SafetyPlanTestID.Hotlines}
      title="Hotlines"
    />
  );
};

export default HotlinesSection;
